import React from 'react';
import { PLACEMENT } from 'baseui/popover';
import { IDummyProps } from './types';
import { Button } from '../Button';
import { overrides as customOverrides, popoverOverrides } from './overrides';
import { List } from '../List';
import { useStyletron } from 'baseui';
import { Popover } from '../Popover';

import * as meta from '../../meta-data.json';

export const View = React.forwardRef((props: IDummyProps, ref: any) => {
	const {
		items = [],
		onItemSelect,
		styleListContainer = {
			width: '300px'
		},
		icon,
		disabled = false,
		multiselect,
		children,
		buttongroupShape,
		className,
		buttonClassName,
		overrides,
		popoverPlacement = PLACEMENT.bottomLeft,
		shape,
		nestedComponent,
		size,
		onKeyDown,
		iconChevron = "keyboard_arrow_down",
		callToClose,
		closeOnScroll,
		ignoreBoundary,
		onClose,
		isCustomSizeList = false,
		...rest
	} = props;

	const _icon: { iconBefore?: any } = icon ? { iconBefore: icon } : {}; //TODO: review types
	const buttonRef = React.createRef<HTMLDivElement>();
	const [css] = useStyletron();

	// The component instance will be extended
	// with whatever you return from the callback passed
	// as the second argument
	React.useImperativeHandle(ref, () => ({
		setClose() {
			setOpen(false);
			if (onClose) {
				onClose();
			}
		}
	}));

	const onKeyDownHandler = (e: React.KeyboardEvent<HTMLButtonElement>) => {
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setOpen(true)
				break;
		}
		onKeyDown && onKeyDown(e);
	};

	const closeDropdownButton = () => {
		setOpen(false);
		if (onClose) {
			onClose();
		}
	}

	const handleScroll = React.useCallback(() => {
		if (closeOnScroll) {
			setOpen(false);
			if (onClose) {
				onClose();
			}
		}
	}, [closeOnScroll]);

	const spanRef = React.useRef<any>(null);
	const [isOpen, setOpen] = React.useState(false);

	React.useEffect(() => {
		setOpen(false);
	}, [callToClose]);

	React.useEffect(() => {
		if (window.self === closeOnScroll) {
			window.addEventListener('scroll', handleScroll);
		} else if (closeOnScroll === "all" || closeOnScroll === true) {
			document.addEventListener('scroll', handleScroll, true);
		} else {
			closeOnScroll?.current?.addEventListener('scroll', handleScroll, true);
		}

	}, [closeOnScroll, handleScroll])

	React.useEffect(() => {
		return () => { // Anything in here is fired on component unmount.
			if (window.self === closeOnScroll) {
				window.removeEventListener('scroll', handleScroll);
			} else if (closeOnScroll === "all" || closeOnScroll === true) {
				document.removeEventListener('scroll', handleScroll, true);
			} else {
				closeOnScroll?.current?.removeEventListener('scroll', handleScroll, true);
			}
		}
	}, [closeOnScroll, handleScroll])

	return (
		<div data-testid='epo-dropdownpanel' ref={buttonRef} className={`${props.className} ${css({ display: 'inline-flex' })}`} {...props.rootProps} {...{
			'data-epo': `epo-dropdownpanel-${meta.version}`,
			part: `epo-dropdownpanel-${meta.version}`,
		}}>
			<Popover
				focusLock
				placement={popoverPlacement}
				mountNode={spanRef.current}
				ignoreBoundary={ignoreBoundary}
				isOpen={isOpen}
				onClick={event => {
					setOpen(!isOpen)
				}}
				onClickOutside={() => {
					closeDropdownButton();
				}}
				onEsc={() => {
					setOpen(false);
					if (onClose) {
						onClose();
					}
				}}
				inverted
				content={() =>
					!props.nestedComponent ? (
						<List
							hideFocusRing
							size={props.size}
							multiselect={multiselect}
							items={items}
							styleListContainer={styleListContainer}
							isCustom={isCustomSizeList}
							onItemSelect={(item: any) => {
								item.event.stopPropagation();

								if (!multiselect) {
									setOpen(false);
								}

								if (onItemSelect) {
									onItemSelect(item);
								}
							}}
							overrides={customOverrides({ props, onClickOutside: closeDropdownButton, onCloseList: closeDropdownButton })}
						/>
					) : (
						nestedComponent
					)
				}
				overrides={popoverOverrides}
			>
				<Button
					disabled={disabled}
					iconAfter={typeof iconChevron == 'string' ? iconChevron as string : iconChevron === true ? "keyboard_arrow_down" : undefined}
					{..._icon}
					{...rest}
					size={size}
					isSelected={isOpen}
					shape={buttongroupShape || shape}
					className={buttonClassName}
					onKeyDown={onKeyDownHandler}
				>
					{children || (icon && ' ')}
				</Button>
			</Popover>
			<span ref={spanRef}></span>
		</div >
	);
});
